import { type DatabaseExternalFile, ExternalFileSource } from '@/lib/types';
import useApi from '@/lib/api/useApi';
import { useContext, useState } from 'react';
import ExternalFilesContext from '../ExternalFilesContext';
import Dialog from '@/components/Dialog';
import { FormattedMessage } from 'react-intl';
import FormattedHtmlMessage from '@/components/FormattedHtmlMessage';
import FileChipRemoveButton from '../FileChip/FileChipRemoveButton';

type RemoveExternalFileProps = {
  externalFile: DatabaseExternalFile;
};

const RemoveExternalFile = ({ externalFile }: RemoveExternalFileProps) => {
  const api = useApi();

  const { externalFilesEndpoint, removeExternalFileFromLocalState } =
    useContext(ExternalFilesContext);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const handleRemoveExternalFile = async (): Promise<void> => {
    setRemoving(true);

    setConfirmationDialogOpen(false);

    await api.delete(`${externalFilesEndpoint}/${externalFile.id}`);

    removeExternalFileFromLocalState(externalFile);

    setRemoving(false);
  };

  return (
    <>
      <FileChipRemoveButton
        onClick={() => setConfirmationDialogOpen(true)}
        disabled={removing}
      />
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        dangerous
      >
        <Dialog.Title>
          <FormattedMessage id="portfolio.challenge.files.external_file.remove.confirmation_dialog.title" />
        </Dialog.Title>
        <Dialog.Content>
          <Dialog.ContentText>
            <FormattedHtmlMessage
              id={`portfolio.challenge.files.external_file.remove.confirmation_dialog.text.${externalFile.externalFile.source === ExternalFileSource.GOOGLE_DRIVE ? 'google_drive' : 'onedrive'}`}
              values={{
                filename: externalFile.externalFile.filename,
              }}
            />
          </Dialog.ContentText>
        </Dialog.Content>
        <Dialog.Actions>
          <Dialog.Actions.CancelButton
            onClick={() => setConfirmationDialogOpen(false)}
          />
          <Dialog.Actions.DeleteButton onClick={handleRemoveExternalFile}>
            <FormattedMessage id="portfolio.challenge.files.external_file.remove.confirmation_dialog.actions.remove" />
          </Dialog.Actions.DeleteButton>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};

export default RemoveExternalFile;
